import 'jquery';
import 'magnific-popup';
import $ from "jquery";

import { convertToHtmlElement, localize } from "./common";

export interface IPopup {
    open(htmlContent: string);
    openRawHtml(htmlContent: string);
    openAndLoadAsync(htmlContentLoader: () => Promise<string>): Promise<void>
    close();
    isOpen(): boolean;
}

export enum PopupWidth {
    Mini = 'mini',
    Small = 'small',
    Medium = 'medium',
    Large = 'large',
    Max = 'max'
}

export interface PopupOptions {
    id: string;
    width: PopupWidth;
    closeOnBgClick?: boolean;
    onClose?: () => void;
}

export default class MagnificPopup implements IPopup {
    private options: PopupOptions;

    constructor(options: PopupOptions = null) {
        this.options = options ? options : { id: null, width: PopupWidth.Large, closeOnBgClick: false };
    }

    public openRawHtml(htmlContent: string) {
        $.magnificPopup.close();

        $.magnificPopup.open({
            items: {
                src: htmlContent,
                type: 'inline'
            }
        });
    }

    public open(htmlContent: string) {
        $.magnificPopup.open({
            items: {
                src: `<div data-popup class="white-popup ${this.getPopupWidthClass()}"${this.renderId()}>${htmlContent}</div>`,
                type: 'inline',
            },
            closeOnBgClick: this.options.closeOnBgClick,
            callbacks: {
                close: () => this.options.onClose?.()
            }
        });
    }


    public async openAndLoadAsync(htmlContentLoader: () => Promise<string>): Promise<void> {
        const popupContentId = `popup_${Date.now()}`;
        $.magnificPopup.open({
            items: {
                src: `<div data-popup class="white-popup ${this.getPopupWidthClass()}"${this.renderId()}><div class="popup-loading" id="${popupContentId}"><div class="loading-icon"><div></div><div></div><div></div></div></div></div>`,
                type: 'inline',
            },
            closeOnBgClick: this.options.closeOnBgClick
        });
        let htmlContent: string = null;
        try {
            htmlContent = await htmlContentLoader();
        }
        catch (error) {
            // HttpClient has already shown its own error dialog (replacing this popup); the fallback
            // below only applies when this popup's loading placeholder is still on screen.
            console.error('Popup content loader failed.', error);
        }

        const contentElement = document.querySelector(`#${popupContentId}`);
        if (!contentElement) {
            return;
        }

        // A body without an HTML element (empty response, plain-text error) must never reach
        // replaceWith — replaceWith(null) renders the literal text "null" inside the popup.
        const resultElement = convertToHtmlElement(htmlContent ?? '');
        contentElement.replaceWith(resultElement ?? this.createLoadFailedElement());
    }

    private createLoadFailedElement(): HTMLElement {
        const element = convertToHtmlElement(`
            <div class="message-dialogue">
                <h2 class="message-dialogue__caption">${localize('Warning')}</h2>
                <div class="message-dialogue__text">
                    <p>${localize('AjaxCallFailGeneralMessage')}</p>
                </div>
                <div class="message-dialogue__controls">
                    <a href="#" class="message-dialogue__button configurable-base-primary" data-component-part="close-button">
                        <span>${localize('Close')}</span>
                    </a>
                </div>
            </div>`);
        element.querySelector('[data-component-part="close-button"]')
            .addEventListener('click', event => {
                event.preventDefault();
                this.close();
            });
        return element;
    }

    public close() {
        $.magnificPopup.close();
    }

    public get isOpen() {
        return $.magnificPopup.instance.isOpen;
    }

    private getPopupWidthClass(): string {
        return this.options.width ? this.options.width.toString() : PopupWidth.Large.toString();
    }

    private renderId(): string {
        if (!this.options.id)
            return '';

        return ` id="${this.options.id}"`;
    }
}
